import { useEffect, useMemo, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import {
  centsToUsdString,
  parseUsdToCents,
  type GetGiftCardResponse,
  type GiftCardBatchSummary,
  type GiftCardStatus,
  type IssueGiftCardRequest,
  type IssueGiftCardResponse,
  type ListGiftCardsQuery,
  type RedeemGiftCardOnBehalfResponse,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useCloseGiftCardBatch,
  useCreateGiftCardBatch,
  useGiftCard,
  useGiftCardBatches,
  useGiftCardList,
  useIssueGiftCard,
  useRedeemOnBehalf,
  useReopenGiftCardBatch,
  useRevokeGiftCard,
} from '../api/useGiftCards.ts';
import { Modal } from '../components/Modal.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useClipboard } from '../hooks/useClipboard.tsx';
import { useT } from '../i18n/index.ts';
import {
  INPUT_CLASS,
  INPUT_CLASS_MONO,
  INPUT_CLASS_MONO_ON_HIGHEST,
  INPUT_CLASS_ON_HIGHEST,
} from '../styles/forms.ts';

const UNBATCHED_SENTINEL = '__unbatched';
const DEFAULT_LIMIT = 25;

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const CNY_FORMATTER = new Intl.NumberFormat('zh-CN', {
  style: 'currency',
  currency: 'CNY',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatUsdCents(cents: number): string {
  if (!Number.isFinite(cents)) return '-';
  return USD_FORMATTER.format(cents / 100);
}

function formatCnyCents(cents: number): string {
  if (!Number.isFinite(cents)) return '-';
  return CNY_FORMATTER.format(cents / 100);
}

function formatUsdInput(cents: number): string {
  return centsToUsdString(cents);
}

function parseCnyToCents(raw: unknown): number | null {
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return null;
    return Math.round(raw * 100);
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return null;
    return Math.round(parsed * 100);
  }
  return null;
}

function formatRate(rate: number): string {
  if (!Number.isFinite(rate)) return '-';
  return rate.toFixed(4);
}

function formatDateTime(value: string | null): string {
  if (value === null) return '-';
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return value;
  return d.toLocaleString();
}

function maskCode(code: string): string {
  if (code.length <= 8) return '••••';
  return `${code.slice(0, 4)}••••••••${code.slice(-4)}`;
}

function statusVariant(status: GiftCardStatus): 'ok' | 'warning' | 'error' | 'neutral' {
  if (status === 'active') return 'ok';
  if (status === 'revoked') return 'error';
  if (status === 'redeemed') return 'neutral';
  return 'warning';
}

function GiftCardStatusChip({ status }: { readonly status: GiftCardStatus }): JSX.Element {
  const t = useT();
  const label =
    status === 'active'
      ? t('gift_cards.chip.active')
      : status === 'expired'
        ? t('gift_cards.chip.expired')
        : status === 'redeemed'
          ? t('gift_cards.chip.redeemed')
          : t('gift_cards.chip.revoked');
  return (
    <StatusChip
      variant={statusVariant(status)}
      label={label}
      testId="gift-card-status"
    />
  );
}

function batchLabel(
  batchId: string | null,
  batchName: string | null | undefined,
  t: ReturnType<typeof useT>,
): string {
  if (batchId === null) return t('gift_cards.batch.unbatched');
  if (batchName !== null && batchName !== undefined && batchName.length > 0) {
    return `${batchName} (${batchId})`;
  }
  return batchId;
}

function dateInputToIso(value: string, endOfDay: boolean): string | undefined {
  if (value.length === 0) return undefined;
  return `${value}T${endOfDay ? '23:59:59' : '00:00:00'}Z`;
}

export function GiftCards(): JSX.Element {
  const t = useT();
  const batches = useGiftCardBatches();
  const activeBatches = useMemo(
    () => (batches.data?.batches ?? []).filter((batch) => batch.status === 'active'),
    [batches.data?.batches],
  );

  return (
    <section data-testid="gift-cards-page" className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <h2 className="text-3xl font-semibold text-on-surface">{t('gift_cards.title')}</h2>
        <p className="text-sm text-outline">{t('gift_cards.subtitle')}</p>
      </header>

      <BatchWorkbench batches={batches.data?.batches ?? []} loading={batches.isLoading} />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(320px,0.8fr)_minmax(0,1.6fr)]">
        <IssueCard activeBatches={activeBatches} />
        <GiftCardList batches={batches.data?.batches ?? []} />
      </div>

      <LookupCard />
    </section>
  );
}

function Panel({
  title,
  children,
  testId,
}: {
  readonly title: string;
  readonly children: ReactNode;
  readonly testId: string;
}): JSX.Element {
  return (
    <article data-testid={testId} className="flex flex-col gap-5 rounded-lg bg-surface-container p-6">
      <header className="flex items-center justify-between gap-4">
        <h3 className="text-sm font-semibold text-on-surface">{title}</h3>
      </header>
      {children}
    </article>
  );
}

// ─── Batches ─────────────────────────────────────────────────────────────

function BatchWorkbench({
  batches,
  loading,
}: {
  readonly batches: readonly GiftCardBatchSummary[];
  readonly loading: boolean;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const create = useCreateGiftCardBatch();
  const close = useCloseGiftCardBatch();
  const reopen = useReopenGiftCardBatch();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const totals = useMemo(
    () => ({
      issued: batches.reduce((sum, batch) => sum + batch.issued_count, 0),
      active: batches.reduce((sum, batch) => sum + batch.active_count, 0),
      redeemed: batches.reduce((sum, batch) => sum + batch.redeemed_count, 0),
      value: batches.reduce((sum, batch) => sum + batch.total_amount_cny_cents, 0),
    }),
    [batches],
  );

  async function handleCreate(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError(t('gift_cards.batches.error.name_required'));
      return;
    }
    try {
      await create.mutateAsync({
        name: trimmed,
        ...(description.trim().length > 0 ? { description: description.trim() } : {}),
      });
      setName('');
      setDescription('');
    } catch (err) {
      const message = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  async function updateBatch(id: string, action: 'close' | 'reopen'): Promise<void> {
    try {
      if (action === 'close') {
        await close.mutateAsync(id);
      } else {
        await reopen.mutateAsync(id);
      }
    } catch (err) {
      const message = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      toast.error(message);
    }
  }

  return (
    <Panel title={t('gift_cards.batches.title')} testId="gift-card-batches-panel">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <BatchMetric label={t('gift_cards.batches.metric.issued')} value={String(totals.issued)} />
        <BatchMetric label={t('gift_cards.batches.metric.active')} value={String(totals.active)} />
        <BatchMetric label={t('gift_cards.batches.metric.redeemed')} value={String(totals.redeemed)} />
        <BatchMetric label={t('gift_cards.batches.metric.value')} value={formatCnyCents(totals.value)} />
      </div>

      <form className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(160px,0.7fr)_minmax(220px,1fr)_auto]" onSubmit={handleCreate}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          placeholder={t('gift_cards.batches.create.name')}
          className={INPUT_CLASS}
          data-testid="gift-card-batch-name"
          disabled={create.isPending}
        />
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          placeholder={t('gift_cards.batches.create.description')}
          className={INPUT_CLASS}
          data-testid="gift-card-batch-description"
          disabled={create.isPending}
        />
        <button
          type="submit"
          disabled={create.isPending}
          data-testid="gift-card-batch-create"
          className="inline-flex min-h-10 items-center justify-center rounded-md bg-primary-container px-4 text-sm font-semibold text-on-primary disabled:pointer-events-none disabled:opacity-40"
        >
          {create.isPending ? t('gift_cards.batches.create.submitting') : t('gift_cards.batches.create.submit')}
        </button>
      </form>
      {error !== null ? <p className="text-sm text-error" role="alert">{error}</p> : null}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
        {loading ? (
          <p className="text-sm text-outline">{t('common.loading')}</p>
        ) : batches.length === 0 ? (
          <p className="text-sm text-outline">{t('gift_cards.batches.empty')}</p>
        ) : (
          batches.map((batch) => (
            <div key={batch.id} className="flex min-h-32 flex-col gap-3 rounded-lg bg-surface-container-low p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-on-surface">{batch.name}</p>
                  <p className="break-all font-mono text-xs text-outline">{batch.id}</p>
                </div>
                <StatusChip
                  variant={batch.status === 'active' ? 'ok' : 'neutral'}
                  label={batch.status === 'active' ? t('gift_cards.batches.status.active') : t('gift_cards.batches.status.closed')}
                />
              </div>
              {batch.description !== null ? (
                <p className="text-sm text-outline">{batch.description}</p>
              ) : null}
              <dl className="grid grid-cols-3 gap-2 text-xs">
                <BatchStat label={t('gift_cards.batches.metric.issued')} value={String(batch.issued_count)} />
                <BatchStat label={t('gift_cards.batches.metric.active')} value={String(batch.active_count)} />
                <BatchStat label={t('gift_cards.batches.metric.redeemed')} value={String(batch.redeemed_count)} />
              </dl>
              <div className="mt-auto flex items-center justify-between gap-3">
                <span className="font-mono text-xs text-outline">{formatCnyCents(batch.total_amount_cny_cents)}</span>
                <button
                  type="button"
                  disabled={close.isPending || reopen.isPending}
                  onClick={() => void updateBatch(batch.id, batch.status === 'active' ? 'close' : 'reopen')}
                  className="rounded-md bg-surface-container-highest px-3 py-2 text-xs font-semibold text-on-surface disabled:pointer-events-none disabled:opacity-40"
                  data-testid={`gift-card-batch-${batch.status === 'active' ? 'close' : 'reopen'}`}
                >
                  {batch.status === 'active' ? t('gift_cards.batches.close') : t('gift_cards.batches.reopen')}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </Panel>
  );
}

function BatchMetric({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="rounded-lg bg-surface-container-low p-4">
      <p className="text-xs text-outline">{label}</p>
      <p className="mt-1 font-mono text-xl font-semibold text-on-surface">{value}</p>
    </div>
  );
}

function BatchStat({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-outline">{label}</dt>
      <dd className="font-mono text-on-surface">{value}</dd>
    </div>
  );
}

// ─── Issue ───────────────────────────────────────────────────────────────

function IssueCard({ activeBatches }: { readonly activeBatches: readonly GiftCardBatchSummary[] }): JSX.Element {
  const t = useT();
  const issue = useIssueGiftCard();
  const toast = useToast();
  const [codeHint, setCodeHint] = useState('');
  const [amountCnyInput, setAmountCnyInput] = useState('');
  const [batchId, setBatchId] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssueGiftCardResponse | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const amountCnyCents = parseCnyToCents(amountCnyInput);
    if (amountCnyCents === null || amountCnyCents <= 0) {
      setError(t('gift_cards.issue.error.amount_positive'));
      return;
    }
    if (amountCnyCents % 100 !== 0) {
      setError(t('gift_cards.issue.error.amount_whole_yuan'));
      return;
    }
    const isoExpiry = dateInputToIso(expiresAt.trim(), true);
    if (isoExpiry !== undefined && Date.parse(isoExpiry) <= Date.now()) {
      setError(t('gift_cards.issue.error.expires_at_future'));
      return;
    }
    const body: IssueGiftCardRequest = {
      amount_cny_cents: amountCnyCents,
      ...(codeHint.trim().length > 0 ? { code_hint: codeHint.trim() } : {}),
      ...(batchId.length > 0 ? { batch_id: batchId } : {}),
      ...(isoExpiry !== undefined ? { expires_at: isoExpiry } : {}),
    };
    try {
      const result = await issue.mutateAsync(body);
      setIssued(result);
      setCodeHint('');
      setAmountCnyInput('');
      setExpiresAt('');
    } catch (err) {
      const message = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Panel title={t('gift_cards.issue.title')} testId="gift-card-issue-card">
      <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
        <label className="flex flex-col gap-2">
          <span className="text-xs font-semibold text-outline">{t('gift_cards.issue.amount.label')}</span>
          <input
            type="number"
            min="0"
            step="1"
            value={amountCnyInput}
            onChange={(e) => setAmountCnyInput(e.currentTarget.value)}
            placeholder={t('gift_cards.issue.amount.placeholder')}
            data-testid="gift-card-issue-amount"
            disabled={issue.isPending}
            className={INPUT_CLASS}
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-xs font-semibold text-outline">{t('gift_cards.issue.batch_id.label')}</span>
          <select
            value={batchId}
            onChange={(e) => setBatchId(e.currentTarget.value)}
            className={INPUT_CLASS}
            data-testid="gift-card-issue-batch-id"
            disabled={issue.isPending}
          >
            <option value="">{t('gift_cards.batch.unbatched')}</option>
            {activeBatches.map((batch) => (
              <option key={batch.id} value={batch.id}>{batch.name}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-xs font-semibold text-outline">{t('gift_cards.issue.code_hint.label')}</span>
          <input
            type="text"
            value={codeHint}
            onChange={(e) => setCodeHint(e.currentTarget.value)}
            placeholder={t('gift_cards.issue.code_hint.placeholder')}
            data-testid="gift-card-issue-code-hint"
            disabled={issue.isPending}
            className={INPUT_CLASS}
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-xs font-semibold text-outline">{t('gift_cards.issue.expires_at.label')}</span>
          <input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.currentTarget.value)}
            data-testid="gift-card-issue-expires-at"
            disabled={issue.isPending}
            className={INPUT_CLASS}
          />
        </label>
        {error !== null ? <p className="text-sm text-error" role="alert" data-testid="gift-card-issue-error">{error}</p> : null}
        <button
          type="submit"
          disabled={issue.isPending}
          data-testid="gift-card-issue-submit"
          className="inline-flex min-h-10 items-center justify-center rounded-md bg-primary-container px-4 text-sm font-semibold text-on-primary disabled:pointer-events-none disabled:opacity-40"
        >
          {issue.isPending ? t('gift_cards.issue.submitting') : t('gift_cards.issue.submit')}
        </button>
      </form>
      {issued !== null ? (
        <IssuedCardSummary
          issued={issued}
          onIssueAnother={() => {
            setIssued(null);
            setCodeHint('');
            setAmountCnyInput('');
            setExpiresAt('');
          }}
        />
      ) : null}
    </Panel>
  );
}

function IssuedCardSummary({
  issued,
  onIssueAnother,
}: {
  readonly issued: IssueGiftCardResponse;
  readonly onIssueAnother: () => void;
}): JSX.Element {
  const t = useT();
  const { copy, copied } = useClipboard();
  return (
    <div className="flex flex-col gap-3 rounded-lg bg-surface-container-low p-4" data-testid="gift-card-issue-result">
      <button
        type="button"
        onClick={() => void copy(issued.code)}
        data-testid="gift-card-issue-code"
        className="rounded-md bg-surface-container-lowest p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <span className="block text-xs text-outline">{t('gift_cards.issued.code_label')}</span>
        <span className="mt-1 block break-all font-mono text-lg font-semibold text-primary">{issued.code}</span>
      </button>
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <BatchStat label={t('gift_cards.issued.face_value')} value={formatCnyCents(issued.amount_cny_cents)} />
        <BatchStat label={t('gift_cards.issued.credit_value')} value={formatUsdCents(issued.amount_usd_cents)} />
        <BatchStat label={t('gift_cards.issued.applied_rate')} value={formatRate(issued.applied_rate)} />
        <BatchStat label={t('gift_cards.issued.batch')} value={issued.batch_id ?? t('gift_cards.batch.unbatched')} />
        <BatchStat label={t('gift_cards.issued.expires_at')} value={formatDateTime(issued.expires_at)} />
      </dl>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-outline" data-testid="gift-card-issue-copy-status">
          {copied ? t('common.copied') : t('common.click_to_copy')}
        </p>
        <button
          type="button"
          onClick={onIssueAnother}
          data-testid="gift-card-issue-another"
          className="rounded-md bg-surface-container-highest px-3 py-2 text-xs font-semibold text-on-surface"
        >
          {t('gift_cards.issued.issue_another')}
        </button>
      </div>
    </div>
  );
}

// ─── List ────────────────────────────────────────────────────────────────

function GiftCardList({ batches }: { readonly batches: readonly GiftCardBatchSummary[] }): JSX.Element {
  const t = useT();
  const { copy } = useClipboard();
  const [batchId, setBatchId] = useState('');
  const [status, setStatus] = useState<'all' | GiftCardStatus>('all');
  const [accountId, setAccountId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);
  const [revealed, setRevealed] = useState<Set<string>>(() => new Set());
  const [selectedCard, setSelectedCard] = useState<GetGiftCardResponse | null>(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);

  const query = useMemo<ListGiftCardsQuery>(() => {
    const fromIso = dateInputToIso(from, false);
    const toIso = dateInputToIso(to, true);
    return {
      ...(batchId.length > 0 ? { batch_id: batchId } : {}),
      ...(status !== 'all' ? { status } : {}),
      ...(accountId.trim().length > 0 ? { account_id: accountId.trim() } : {}),
      ...(fromIso !== undefined ? { from: fromIso } : {}),
      ...(toIso !== undefined ? { to: toIso } : {}),
      limit: DEFAULT_LIMIT,
      offset,
    };
  }, [accountId, batchId, from, offset, status, to]);

  const cards = useGiftCardList(query);
  const total = cards.data?.total ?? 0;
  const rows = cards.data?.gift_cards ?? [];

  function resetPagination(): void {
    setOffset(0);
  }

  function toggleReveal(code: string): void {
    setRevealed((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function openRevoke(card: GetGiftCardResponse): void {
    setSelectedCard(card);
    setRevokeOpen(true);
  }

  function openRedeem(card: GetGiftCardResponse): void {
    setSelectedCard(card);
    setRedeemOpen(true);
  }

  return (
    <Panel title={t('gift_cards.list.title')} testId="gift-card-list-panel">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
        <select
          value={batchId}
          onChange={(e) => { setBatchId(e.currentTarget.value); resetPagination(); }}
          className={INPUT_CLASS}
          data-testid="gift-card-list-batch-filter"
        >
          <option value="">{t('gift_cards.list.batch.all')}</option>
          <option value={UNBATCHED_SENTINEL}>{t('gift_cards.batch.unbatched')}</option>
          {batches.map((batch) => (
            <option key={batch.id} value={batch.id}>{batch.name}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => { setStatus(e.currentTarget.value as 'all' | GiftCardStatus); resetPagination(); }}
          className={INPUT_CLASS}
          data-testid="gift-card-list-status-filter"
        >
          <option value="all">{t('gift_cards.list.status.all')}</option>
          <option value="active">{t('gift_cards.chip.active')}</option>
          <option value="expired">{t('gift_cards.chip.expired')}</option>
          <option value="redeemed">{t('gift_cards.chip.redeemed')}</option>
          <option value="revoked">{t('gift_cards.chip.revoked')}</option>
        </select>
        <input
          type="text"
          value={accountId}
          onChange={(e) => { setAccountId(e.currentTarget.value); resetPagination(); }}
          placeholder={t('gift_cards.list.account.placeholder')}
          className={INPUT_CLASS_MONO}
          data-testid="gift-card-list-account-filter"
        />
        <input
          type="date"
          value={from}
          onChange={(e) => { setFrom(e.currentTarget.value); resetPagination(); }}
          className={INPUT_CLASS}
          data-testid="gift-card-list-from-filter"
        />
        <input
          type="date"
          value={to}
          onChange={(e) => { setTo(e.currentTarget.value); resetPagination(); }}
          className={INPUT_CLASS}
          data-testid="gift-card-list-to-filter"
        />
      </div>

      {cards.isError ? (
        <p className="rounded-lg bg-error-container px-4 py-3 text-sm text-on-error-container" role="alert">
          {cards.error instanceof OpsApiError ? `${cards.error.code}: ${cards.error.message}` : coerceErrorMessage(cards.error)}
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-outline-variant/30">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-surface-container-low text-xs text-outline">
            <tr>
              <th className="px-4 py-3 font-semibold">{t('gift_cards.list.column.code')}</th>
              <th className="px-4 py-3 font-semibold">{t('gift_cards.list.column.status')}</th>
              <th className="px-4 py-3 font-semibold">{t('gift_cards.list.column.amount')}</th>
              <th className="px-4 py-3 font-semibold">{t('gift_cards.list.column.batch')}</th>
              <th className="px-4 py-3 font-semibold">{t('gift_cards.list.column.redeemed_by')}</th>
              <th className="px-4 py-3 font-semibold">{t('gift_cards.list.column.updated')}</th>
              <th className="px-4 py-3 text-right font-semibold">{t('gift_cards.list.column.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {cards.isLoading ? (
              <tr><td className="px-4 py-6 text-outline" colSpan={7}>{t('common.loading')}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td className="px-4 py-6 text-outline" colSpan={7}>{t('gift_cards.list.empty')}</td></tr>
            ) : rows.map((card) => {
              const isRevealed = revealed.has(card.code);
              const canRevoke = card.status === 'active' || card.status === 'expired';
              const canRedeem = card.status === 'active';
              return (
                <tr key={card.code} className="border-t border-outline-variant/20">
                  <td className="px-4 py-3">
                    <div className="flex min-w-56 items-center gap-2">
                      <span className="break-all font-mono text-xs text-on-surface" data-testid="gift-card-list-code">
                        {isRevealed ? card.code : maskCode(card.code)}
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleReveal(card.code)}
                        className="rounded-md bg-surface-container-high px-2 py-1 text-xs text-on-surface"
                      >
                        {isRevealed ? t('gift_cards.list.hide_code') : t('gift_cards.list.show_code')}
                      </button>
                      <button
                        type="button"
                        onClick={() => void copy(card.code)}
                        className="rounded-md bg-surface-container-high px-2 py-1 text-xs text-on-surface"
                      >
                        {t('gift_cards.list.copy_code')}
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3"><GiftCardStatusChip status={card.status} /></td>
                  <td className="px-4 py-3 font-mono">{formatCnyCents(card.amount_cny_cents)}</td>
                  <td className="px-4 py-3 text-outline">{batchLabel(card.batch_id, card.batch_name, t)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-outline">{card.redeemed_by_account_id ?? '-'}</td>
                  <td className="px-4 py-3 text-xs text-outline">
                    {formatDateTime(card.redeemed_at ?? card.revoked_at ?? card.issued_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        disabled={!canRevoke}
                        onClick={() => openRevoke(card)}
                        className="rounded-md bg-error-container px-3 py-2 text-xs font-semibold text-on-error-container disabled:pointer-events-none disabled:opacity-40"
                      >
                        {t('gift_cards.result.revoke')}
                      </button>
                      <button
                        type="button"
                        disabled={!canRedeem}
                        onClick={() => openRedeem(card)}
                        className="rounded-md bg-primary-container px-3 py-2 text-xs font-semibold text-on-primary disabled:pointer-events-none disabled:opacity-40"
                      >
                        {t('gift_cards.result.redeem')}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-outline">
        <span>{t('gift_cards.list.total', { total: String(total) })}</span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - DEFAULT_LIMIT))}
            className="rounded-md bg-surface-container-highest px-3 py-2 text-on-surface disabled:pointer-events-none disabled:opacity-40"
          >
            {t('common.previous')}
          </button>
          <button
            type="button"
            disabled={offset + DEFAULT_LIMIT >= total}
            onClick={() => setOffset(offset + DEFAULT_LIMIT)}
            className="rounded-md bg-surface-container-highest px-3 py-2 text-on-surface disabled:pointer-events-none disabled:opacity-40"
          >
            {t('common.next')}
          </button>
        </div>
      </div>

      {revokeOpen && selectedCard !== null ? (
        <RevokeModal card={selectedCard} onClose={() => setRevokeOpen(false)} onSuccess={() => setRevokeOpen(false)} />
      ) : null}
      {redeemOpen && selectedCard !== null ? (
        <RedeemModal card={selectedCard} onClose={() => setRedeemOpen(false)} onSuccess={() => setRedeemOpen(false)} />
      ) : null}
    </Panel>
  );
}

// ─── Lookup ──────────────────────────────────────────────────────────────

function LookupCard(): JSX.Element {
  const t = useT();
  const [draft, setDraft] = useState('');
  const [submitted, setSubmitted] = useState<string | null>(null);
  const lookup = useGiftCard(submitted ?? undefined, { enabled: submitted !== null });
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (toast === null) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const trimmed = draft.trim();
    if (trimmed.length > 0) setSubmitted(trimmed);
  }

  return (
    <Panel title={t('gift_cards.lookup.title')} testId="gift-card-lookup-card">
      <form className="flex flex-col gap-3 sm:flex-row sm:items-end" onSubmit={handleSubmit} noValidate>
        <label className="flex flex-1 flex-col gap-2">
          <span className="text-xs font-semibold text-outline">{t('gift_cards.lookup.code.label')}</span>
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            placeholder={t('gift_cards.lookup.code.placeholder')}
            data-testid="gift-card-lookup-input"
            disabled={lookup.isFetching}
            className={INPUT_CLASS_MONO}
            autoComplete="off"
          />
        </label>
        <button
          type="submit"
          disabled={lookup.isFetching || draft.trim().length === 0}
          data-testid="gift-card-lookup-submit"
          className="inline-flex min-h-10 items-center justify-center rounded-md bg-primary-container px-4 text-sm font-semibold text-on-primary disabled:pointer-events-none disabled:opacity-40"
        >
          {lookup.isFetching ? t('gift_cards.lookup.submitting') : t('gift_cards.lookup.submit')}
        </button>
      </form>

      {submitted !== null && lookup.isError ? (
        <p className="rounded-lg bg-error-container px-4 py-3 text-sm text-on-error-container" role="alert" data-testid="gift-card-lookup-error">
          {lookup.error instanceof OpsApiError ? `${lookup.error.code}: ${lookup.error.message}` : t('gift_cards.lookup.error.network')}
        </p>
      ) : null}

      {submitted !== null && lookup.isSuccess ? (
        <LookupResult
          card={lookup.data}
          onRevoke={() => setRevokeOpen(true)}
          onRedeem={() => setRedeemOpen(true)}
        />
      ) : null}

      {revokeOpen && lookup.data !== undefined ? (
        <RevokeModal
          card={lookup.data}
          onClose={() => setRevokeOpen(false)}
          onSuccess={(message) => {
            setRevokeOpen(false);
            setToast(message);
          }}
        />
      ) : null}
      {redeemOpen && lookup.data !== undefined ? (
        <RedeemModal
          card={lookup.data}
          onClose={() => setRedeemOpen(false)}
          onSuccess={(message) => {
            setRedeemOpen(false);
            setToast(message);
          }}
        />
      ) : null}

      {toast !== null ? <p role="status" data-testid="gift-card-toast" className="text-sm text-primary">{toast}</p> : null}
    </Panel>
  );
}

function LookupResult({
  card,
  onRevoke,
  onRedeem,
}: {
  readonly card: GetGiftCardResponse;
  readonly onRevoke: () => void;
  readonly onRedeem: () => void;
}): JSX.Element {
  const t = useT();
  const canRevoke = card.status === 'active' || card.status === 'expired';
  const canRedeem = card.status === 'active';

  return (
    <div className="flex flex-col gap-4 rounded-lg bg-surface-container-low p-4" data-testid="gift-card-lookup-result">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="break-all font-mono text-lg font-semibold text-primary" data-testid="gift-card-lookup-code">
          {card.code}
        </span>
        <GiftCardStatusChip status={card.status} />
      </div>
      <dl className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <BatchStat label={t('gift_cards.result.face_value')} value={formatCnyCents(card.amount_cny_cents)} />
        <BatchStat label={t('gift_cards.result.credit_value')} value={formatUsdCents(card.amount_usd_cents)} />
        <BatchStat label={t('gift_cards.result.applied_rate')} value={formatRate(card.applied_rate)} />
        <BatchStat label={t('gift_cards.result.batch_id')} value={batchLabel(card.batch_id, card.batch_name, t)} />
        <BatchStat label={t('gift_cards.result.issued_at')} value={formatDateTime(card.issued_at)} />
        <BatchStat label={t('gift_cards.result.expires_at')} value={formatDateTime(card.expires_at)} />
        <BatchStat label={t('gift_cards.result.redeemed_by')} value={card.redeemed_by_account_id ?? '-'} />
        <BatchStat label={t('gift_cards.list.column.source')} value={card.redeem_source ?? '-'} />
        <BatchStat label={t('gift_cards.result.redeemed_at')} value={formatDateTime(card.redeemed_at)} />
        <BatchStat label={t('gift_cards.result.revoked_at')} value={formatDateTime(card.revoked_at)} />
      </dl>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          disabled={!canRevoke}
          onClick={onRevoke}
          data-testid="gift-card-lookup-revoke"
          className="rounded-md bg-error-container px-4 py-2 text-sm font-semibold text-on-error-container disabled:pointer-events-none disabled:opacity-40"
        >
          {t('gift_cards.result.revoke')}
        </button>
        <button
          type="button"
          disabled={!canRedeem}
          onClick={onRedeem}
          data-testid="gift-card-lookup-redeem"
          className="rounded-md bg-primary-container px-4 py-2 text-sm font-semibold text-on-primary disabled:pointer-events-none disabled:opacity-40"
        >
          {t('gift_cards.result.redeem')}
        </button>
      </div>
    </div>
  );
}

// ─── Modals ──────────────────────────────────────────────────────────────

function RevokeModal({
  card,
  onClose,
  onSuccess,
}: {
  readonly card: GetGiftCardResponse;
  readonly onClose: () => void;
  readonly onSuccess: (message: string) => void;
}): JSX.Element {
  const t = useT();
  const revoke = useRevokeGiftCard();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await revoke.mutateAsync({
        code: card.code,
        body: reason.trim().length > 0 ? { reason: reason.trim() } : {},
      });
      onSuccess(t('gift_cards.revoke_modal.toast', { code: card.code }));
    } catch (err) {
      const message = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Modal isOpen onClose={() => { if (!revoke.isPending) onClose(); }} titleId="revoke-modal-title" testId="gift-card-revoke-modal">
      <form className="flex flex-col gap-5 p-6" onSubmit={handleSubmit} noValidate>
        <h2 id="revoke-modal-title" className="text-base font-semibold text-on-surface">{t('gift_cards.revoke_modal.title')}</h2>
        <p className="text-sm text-outline">
          {t('gift_cards.revoke_modal.body_prefix')}
          <span className="font-mono font-semibold text-on-surface">{card.code}</span>
          {t('gift_cards.revoke_modal.body_separator')}
          {formatCnyCents(card.amount_cny_cents)}
          {t('gift_cards.revoke_modal.body_suffix')}
        </p>
        <label className="flex flex-col gap-2">
          <span className="text-xs font-semibold text-outline">{t('gift_cards.revoke_modal.reason.label')}</span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            placeholder={t('gift_cards.revoke_modal.reason.placeholder')}
            data-testid="gift-card-revoke-reason"
            disabled={revoke.isPending}
            className={INPUT_CLASS_ON_HIGHEST}
          />
        </label>
        {error !== null ? <p className="text-sm text-error" role="alert" data-testid="gift-card-revoke-error">{error}</p> : null}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} disabled={revoke.isPending} className="rounded-md bg-surface-container px-4 py-2 text-sm font-semibold text-on-surface disabled:pointer-events-none disabled:opacity-40">
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={revoke.isPending} data-testid="gift-card-revoke-submit" className="rounded-md bg-error-container px-4 py-2 text-sm font-semibold text-on-error-container disabled:pointer-events-none disabled:opacity-40">
            {revoke.isPending ? t('gift_cards.revoke_modal.submitting') : t('gift_cards.revoke_modal.submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function RedeemModal({
  card,
  onClose,
  onSuccess,
}: {
  readonly card: GetGiftCardResponse;
  readonly onClose: () => void;
  readonly onSuccess: (message: string) => void;
}): JSX.Element {
  const t = useT();
  const redeem = useRedeemOnBehalf();
  const toast = useToast();
  const [accountId, setAccountId] = useState('');
  const [confirmAmount, setConfirmAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const externalRequestId = useMemo(() => crypto.randomUUID(), []);
  const expectedAmount = card.amount_usd_cents;
  const expectedAmountStr = formatUsdInput(expectedAmount);
  const faceValue = formatCnyCents(card.amount_cny_cents);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmedAccount = accountId.trim();
    if (trimmedAccount.length === 0) {
      setError(t('gift_cards.redeem_modal.error.account_required'));
      return;
    }
    const typedAmountCents = parseUsdToCents(confirmAmount);
    if (typedAmountCents === null || typedAmountCents !== expectedAmount) {
      setError(t('gift_cards.redeem_modal.error.amount_mismatch', { amount: expectedAmountStr }));
      return;
    }
    try {
      const result: RedeemGiftCardOnBehalfResponse = await redeem.mutateAsync({
        code: card.code,
        account_id: trimmedAccount,
        external_request_id: externalRequestId,
        ...(reason.trim().length > 0 ? { reason: reason.trim() } : {}),
      });
      onSuccess(t('gift_cards.redeem_modal.toast', {
        amount: formatUsdCents(result.amount_usd_cents),
        account: result.account_id,
        balance: formatUsdCents(result.new_balance_usd_cents),
      }));
    } catch (err) {
      const message = err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Modal isOpen onClose={() => { if (!redeem.isPending) onClose(); }} titleId="redeem-modal-title" testId="gift-card-redeem-modal">
      <form className="flex flex-col gap-5 p-6" onSubmit={handleSubmit} noValidate>
        <h2 id="redeem-modal-title" className="text-base font-semibold text-on-surface">{t('gift_cards.redeem_modal.title')}</h2>
        <p className="rounded-lg bg-surface-container-low p-4 text-sm text-outline">
          {t('gift_cards.redeem_modal.body_prefix')}
          <span className="font-mono font-semibold text-primary">{formatUsdCents(expectedAmount)}</span>
          {t('gift_cards.redeem_modal.body_middle')}
          <span className="font-mono font-semibold text-on-surface">{card.code}</span>
          {t('gift_cards.redeem_modal.body_suffix', { faceValue })}
        </p>
        <label className="flex flex-col gap-2">
          <span className="text-xs font-semibold text-outline">{t('gift_cards.redeem_modal.account.label')}</span>
          <input
            type="text"
            value={accountId}
            onChange={(e) => setAccountId(e.currentTarget.value)}
            placeholder={t('gift_cards.redeem_modal.account.placeholder')}
            data-testid="gift-card-redeem-account-id"
            disabled={redeem.isPending}
            className={INPUT_CLASS_MONO_ON_HIGHEST}
            autoComplete="off"
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-xs font-semibold text-outline">{t('gift_cards.redeem_modal.confirm.label', { amount: expectedAmountStr })}</span>
          <input
            type="text"
            inputMode="decimal"
            value={confirmAmount}
            onChange={(e) => setConfirmAmount(e.currentTarget.value)}
            placeholder={expectedAmountStr}
            data-testid="gift-card-redeem-confirm-amount"
            disabled={redeem.isPending}
            className={INPUT_CLASS_MONO_ON_HIGHEST}
            autoComplete="off"
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="text-xs font-semibold text-outline">{t('gift_cards.redeem_modal.reason.label')}</span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            placeholder={t('gift_cards.redeem_modal.reason.placeholder')}
            data-testid="gift-card-redeem-reason"
            disabled={redeem.isPending}
            className={INPUT_CLASS_ON_HIGHEST}
          />
        </label>
        <p className="break-all font-mono text-xs text-outline">{t('gift_cards.redeem_modal.idempotency')} {externalRequestId}</p>
        {error !== null ? <p className="text-sm text-error" role="alert" data-testid="gift-card-redeem-error">{error}</p> : null}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} disabled={redeem.isPending} className="rounded-md bg-surface-container px-4 py-2 text-sm font-semibold text-on-surface disabled:pointer-events-none disabled:opacity-40">
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={redeem.isPending} data-testid="gift-card-redeem-submit" className="rounded-md bg-primary-container px-4 py-2 text-sm font-semibold text-on-primary disabled:pointer-events-none disabled:opacity-40">
            {redeem.isPending ? t('gift_cards.redeem_modal.submitting') : t('gift_cards.redeem_modal.submit', { amount: formatUsdCents(expectedAmount) })}
          </button>
        </div>
      </form>
    </Modal>
  );
}
